import { setActiveBulkView, setCampaignIdForLogsView, setBulkLogsViewActiveTab, setBulkLogsViewActiveFilter, BulkLogsViewFilterType } from '@common-reducers/CampaignReducer';
import { getStuckMessagesByCampaignId, selectCampaignMessageStats, selectFilteredCampaignLogs, selectBulkLogsViewActiveTab, selectBulkLogsViewActiveFilter, selectFilteredCampaignLogsByFilter, selectShouldShowRetryAll, selectMessagesToRetry } from '@common-reducers/CampaignSelectors';
import { getCampaignRealStatus, fetchCampaignForLogsViewThunk, fetchCampaignStatsThunk, fetchCampaignDeliveryEvidenceThunk } from '@common-reducers/CampaignThunks';
import { campaignsSelectOneObjectByQuery, DBMessageLogThunks } from "@common-reducers/DBServiceThunks";
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectScheduledMessagesInRedisAndInDBSelector } from '@common/reducers/UserMessageSelectors';
import { ChevronLeft, RefreshCw, Megaphone } from 'lucide-react';
import { useEffect, useCallback, useMemo, useRef } from 'react';
import { waQueryContactsAndChatsTool } from '@common/tools/whatsapp';
import InProgressCampaignIndicator from '../in-progress-campaign-indicator';
import CampaignMessageListContainer from './campaign-message-logs/campaign-message-list-container';
import CampaignMessageLogStatusFilter from './campaign-message-logs/campaign-message-log-status-filter';
import CampaignMessagesWaitingToBeSent from './campaign-message-logs/campaign-messages-waiting-to-be-sent';
import CampaignSentLogs from './campaign-message-logs/campaign-sent-logs';
import ExportCampaignLogsButton from './campaign-message-logs/export-campaign-logs-button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { isExtensionContext } from '@common-services/detect-context';
import { retrySendMessageThunk } from '@extension/context/content/thunks/WhatsappSendMessageThunk';
import { useRetryMessagesBatchMutation, useCampaignFailureBreakdownQuery } from '@common-reducers/backend-api/backend-api';
import NotOnWhatsAppBanner from '../not-on-whatsapp-banner';
import RejectedGroupsRow from '../rejected-groups-row';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { AudAvatar, StatusLabel, Eyebrow } from '@extension/context/content/components/campaigns-redesign/primitives';
import { mapCampaignStatusToDrip } from '@extension/context/content/components/campaigns-redesign/sequence-detail';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

// Component
interface BulkLogsViewProps {
    /** When provided (campaigns-redesign route context), the header back button
     *  calls this instead of the legacy activeBulkView swap — so back returns to
     *  the redesign's campaign list rather than clearing state into a blank
     *  route-driven surface. */
    onBack?: () => void;
}

// Stable empty-array reference so an empty selection never changes identity between renders.
const EMPTY_ARR: any[] = [];

export default function BulkLogsView({ onBack }: BulkLogsViewProps = {}) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);

    // Selectors - get state from Redux
    const campaignIdForLogsView = useAppSelector((state) => state.CampaignReducer.campaignIdForLogsView);
    const activeTab = useAppSelector(selectBulkLogsViewActiveTab);
    const activeFilter = useAppSelector(selectBulkLogsViewActiveFilter);
    const messageStats = useAppSelector(state => selectCampaignMessageStats(state, campaignIdForLogsView ?? ''));
    const messagesWaitingToBeSent = useAppSelector((state) => selectScheduledMessagesInRedisAndInDBSelector(state, campaignIdForLogsView ?? ''));
    const stuckMessages = useAppSelector(state => getStuckMessagesByCampaignId(state, campaignIdForLogsView));
    const campaign = useAppSelector((state) => campaignsSelectOneObjectByQuery(state.DBCampaignReducer, { _id: campaignIdForLogsView }));
    
    // Use selector to determine if "Retry All" button should be shown
    const shouldShowRetryAll = useAppSelector((state) => 
        campaignIdForLogsView ? selectShouldShowRetryAll(state, campaignIdForLogsView) : false
    );

    // Get messages that should be retried (using the same selector logic as individual rows)
    const messagesToRetry = useAppSelector((state) => 
        campaignIdForLogsView ? selectMessagesToRetry(state, campaignIdForLogsView) : []
    );

    // Get all user messages from Redux state for retry
    const allUserMessages = useAppSelector((state) => state.DBUserMessageReducer.entities);

    // RTK Query mutation for batch retry (non-extension context)
    const [retryMessagesBatch, { isLoading: isRetryingAll }] = useRetryMessagesBatchMutation();

    // Handle retry all messages that should show retry button
    const handleRetryAll = useCallback(async () => {
        if (messagesToRetry.length === 0) return;

        try {
            const isExtension = isExtensionContext();
            const messageIdsToRetry = messagesToRetry
                .map(log => log.messageId)
                .filter(Boolean) as string[];

            if (isExtension) {
                // In extension context, get UserMessages and use retrySendMessageThunk
                for (const messageId of messageIdsToRetry) {
                    const userMessage = allUserMessages[messageId] as UserMessage | undefined;

                    if (userMessage) {
                        await dispatch(retrySendMessageThunk({ 
                            userMessage, 
                            retryTrigger: 'manual' 
                        }));
                    } else {
                        // If message not in store, try to fetch it first
                        console.warn(`UserMessage ${messageId} not found in store, skipping retry`);
                    }
                }
            } else {
                // In non-extension context (website), use RTK Query mutation
                await retryMessagesBatch({
                    messageIds: messageIdsToRetry
                }).unwrap();
            }
        } catch (error) {
            console.error('Error retrying all messages:', error);
        }
    }, [messagesToRetry, dispatch, allUserMessages, retryMessagesBatch]);

    // Data fetching - dispatch thunks
    useEffect(() => {
        if (!campaignIdForLogsView) return;
        dispatch(fetchCampaignForLogsViewThunk(campaignIdForLogsView));
    }, [campaignIdForLogsView, dispatch]);

    useEffect(() => {
        if (!campaignIdForLogsView) return;
        dispatch(fetchCampaignStatsThunk(campaignIdForLogsView));
    }, [campaignIdForLogsView, dispatch]);

    // Fetch message logs for the campaign logs list
    useEffect(() => {
        if (!campaignIdForLogsView) return;
        dispatch(DBMessageLogThunks.find({
            campaignId: campaignIdForLogsView,
            $sort: { createdAt: -1 },
            $limit: -1,
        }));
    }, [campaignIdForLogsView, dispatch]);

    // BE#658 (4a): the representative messageIds for the loaded log rows (one per contact). The
    // confirmed userMessage that proves delivery on a before-wa-send-only trail isn't in the log,
    // so fetch its evidence SCOPED by _id. Dependency is a STABLE sorted-id string (not the logs
    // array ref), and the thunk only fetches ids missing from the cache — so a new log event that
    // adds no new messageIds, or reopening the campaign, dispatches nothing and cannot loop.
    const representativeLogs = useAppSelector((state) =>
        campaignIdForLogsView ? selectFilteredCampaignLogs(state, campaignIdForLogsView, null) : EMPTY_ARR
    );
    const evidenceMessageIdsKey = useMemo(
        () => Array.from(new Set((representativeLogs ?? []).map((l: any) => l?.messageId).filter(Boolean))).sort().join(','),
        [representativeLogs]
    );
    const lastEvidenceKeyRef = useRef<string>('');
    useEffect(() => {
        if (!campaignIdForLogsView || !evidenceMessageIdsKey) return;
        if (lastEvidenceKeyRef.current === evidenceMessageIdsKey) return; // no new ids → no dispatch
        lastEvidenceKeyRef.current = evidenceMessageIdsKey;
        dispatch(fetchCampaignDeliveryEvidenceThunk(evidenceMessageIdsKey.split(',')));
    }, [campaignIdForLogsView, evidenceMessageIdsKey, dispatch]);

    useEffect(() => {
        waQueryContactsAndChatsTool({});
    }, [dispatch]);

    const handleBack = useCallback(() => {
        if (onBack) {
            // Redesign route context: let the router return to the campaign list.
            // CampaignLogsRoute's unmount effect resets the Redux logs flags.
            onBack();
            return;
        }
        dispatch(setCampaignIdForLogsView(''));
        dispatch(setActiveBulkView("campaignManagementView"));
    }, [dispatch, onBack]);

    const handleTabChange = useCallback((value: string) => {
        dispatch(setBulkLogsViewActiveTab(parseInt(value)));
    }, [dispatch]);

    const handleFilterClick = useCallback((filter: BulkLogsViewFilterType) => {
        const newFilter = activeFilter === filter ? null : filter;
        dispatch(setBulkLogsViewActiveFilter(newFilter));
    }, [dispatch, activeFilter]);

    // BE#710 — server-side failure breakdown (accurate on a lazy-loaded list) for the
    // "numbers aren't on WhatsApp" banner. Skipped until a campaign is selected.
    const { data: failureBreakdown } = useCampaignFailureBreakdownQuery(
        { campaignId: campaignIdForLogsView ?? '' },
        { skip: !campaignIdForLogsView },
    );

    // "View these numbers" — narrow the recipient list to the not-on-WhatsApp failures
    // (the Delivery-logs / Sent tab is where the per-contact rows render).
    const handleViewNotOnWhatsApp = useCallback(() => {
        dispatch(setBulkLogsViewActiveFilter('not_on_whatsapp'));
        dispatch(setBulkLogsViewActiveTab(0));
    }, [dispatch]);

    const filterSelector = useCallback((state: any) => {
        return selectFilteredCampaignLogsByFilter(state, campaignIdForLogsView ?? '');
    }, [campaignIdForLogsView]);

    if (!campaign || !campaignIdForLogsView) {
        return null;
    }

    const realStatus = getCampaignRealStatus(campaign.campaignStatus ?? '', !!campaign.contactCount);
    // Everything that reached the wire: sent (pre-confirmation) + confirmed (server
    // acked, no receipt yet) + delivered + read. `confirmed` is now its own bucket
    // (split out of `sent`), so it must be added back here to keep the headline count.
    const sentCount = messageStats.sent + messageStats.confirmed + messageStats.delivered + messageStats.read;

    return (
        <div className="col" style={{ display: 'flex', flexDirection: 'column', width: '100%', flex: 1, minHeight: 0, background: 'var(--bg)' }}>
            {/* Header — matches the campaign detail surface: back link, avatar, name +
                status pill, and a quiet meta line (message count + started date). */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--line)', background: 'var(--surface)', flexShrink: 0 }}>
                <button type="button" onClick={handleBack} className="btn ghost sm" style={{ marginBottom: 10, paddingLeft: 4 }}>
                    <ChevronLeft size={14} /> {t('campaignLogs.title')}
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <AudAvatar color="green" icon={Megaphone} size={42} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <h1 style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--ink)', margin: 0 }}>
                                {campaign?.name ?? 'Campaign'}
                            </h1>
                            {campaign?.campaignStatus && <StatusLabel status={mapCampaignStatusToDrip(campaign.campaignStatus)} />}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
                            <span>Delivery logs</span><span>·</span>
                            <span className="num">{messageStats.total.toLocaleString()} messages</span>
                        </div>
                    </div>
                    {campaign?.contactCount && (realStatus === 'Running' || realStatus === 'Paused') && (
                        <InProgressCampaignIndicator campaignId={campaign._id} />
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="scroll" style={{ flex: 1, overflowY: 'auto', padding: '18px 24px 28px' }}>
                <div style={{ maxWidth: 760, margin: '0 auto' }}>
                    <Eyebrow style={{ marginBottom: 10 }}>Delivery status</Eyebrow>

                    {/* BE#710 — "N numbers aren't on WhatsApp" hint (missing country code). */}
                    <NotOnWhatsAppBanner
                        campaignId={campaignIdForLogsView}
                        breakdown={failureBreakdown}
                        onViewNumbers={handleViewNotOnWhatsApp}
                    />

                    {/* BE#613 — groups WhatsApp keeps rejecting (chronic only). */}
                    <RejectedGroupsRow breakdown={failureBreakdown} />

                    {/* Filters and Stats — compact clickable stat strip */}
                    <CampaignMessageLogStatusFilter
                        messageStats={messageStats}
                        activeFilter={activeFilter}
                        onFilterClick={handleFilterClick}
                        notOnWhatsAppCount={failureBreakdown?.notOnWhatsAppCount ?? 0}
                    />

                    {/* Tabs */}
                    <Tabs value={activeTab.toString()} onValueChange={handleTabChange} className="w-full">
                        <div className="flex items-center justify-between gap-4 mb-3">
                            <TabsList className="bg-slate-100 dark:bg-slate-800">
                                <TabsTrigger value="0" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
                                    <span className="pr-2">{t('campaignLogs.sent')}</span>
                                    {sentCount > 0 && (
                                        <Badge variant="secondary" className="ml-1">
                                            {sentCount > 99999 ? '99999+' : sentCount}
                                        </Badge>
                                    )}
                                </TabsTrigger>
                                <TabsTrigger value="1" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
                                    <span className="pr-2">{t('campaignLogs.waitingToBeSent')}</span>
                                    {messagesWaitingToBeSent.length > 0 && (
                                        <Badge variant="secondary" className="ml-1">
                                            {messagesWaitingToBeSent.length > 99999 ? '99999+' : messagesWaitingToBeSent.length}
                                        </Badge>
                                    )}
                                </TabsTrigger>
                                {stuckMessages?.length > 0 && (
                                    <TabsTrigger value="2" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
                                        <span className="pr-2">{t('campaignLogs.stuckMessages')}</span>
                                        <Badge variant="destructive" className="ml-1">
                                            {stuckMessages.length > 99999 ? '99999+' : stuckMessages.length}
                                        </Badge>
                                    </TabsTrigger>
                                )}
                            </TabsList>

                            <div className="flex items-center gap-2">
                                {/* Retry All Button - Show when any message should show retry button */}
                                {shouldShowRetryAll && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleRetryAll}
                                        disabled={isRetryingAll}
                                        className="gap-2"
                                    >
                                        {isRetryingAll ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                <span>{t('campaignLogs.retrying')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw className="h-4 w-4" />
                                                <span>{t('campaignLogs.retryAll')} ({messagesToRetry.length})</span>
                                            </>
                                        )}
                                    </Button>
                                )}

                                {/* Export campaign logs to Excel */}
                                <ExportCampaignLogsButton
                                    campaignId={campaignIdForLogsView}
                                    campaignName={campaign?.name}
                                />
                            </div>
                        </div>

                        {/* Tab Content */}
                        <TabsContent value="0">
                            <CampaignSentLogs
                                campaignId={campaignIdForLogsView}
                                filterSelector={filterSelector}
                            />
                        </TabsContent>
                        <TabsContent value="1" className="mt-4">
                            <CampaignMessagesWaitingToBeSent
                                logs={messagesWaitingToBeSent}
                            />
                        </TabsContent>
                        {stuckMessages?.length > 0 && (
                            <TabsContent value="2" className="mt-4">
                                <CampaignMessageListContainer
                                    logsData={stuckMessages}
                                />
                            </TabsContent>
                        )}
                    </Tabs>
                </div>
            </div>
        </div>
    );
}